/**
 * Tell the support extension what is on screen (#512, item 2).
 *
 * The standalone browser extension reads `<meta name="solum-context">`,
 * and **nothing has ever written one**. So every capture arrived saying only
 * which app it came from — and triage had to guess the module, which is the
 * work the field exists to remove. `feedback.ingest_capture` already takes
 * `p_product_id` and `p_project_id`, so a declared module can land a capture
 * attributed to the right product instead of being sorted by hand.
 *
 * ── a meta tag rewritten on navigation, not a page global ──────────────────
 * The extension's own note argues it, and it is right: the probe already reads
 * meta, a meta tag survives being wrong, and **a page that lies can only
 * mislabel its own capture**. A global would be a second channel to keep in step
 * with the first.
 *
 * ── every field is a claim, and claims are not evidence ────────────────────
 * This is the page describing itself. It is not authorization and not identity —
 * the capture is written by a signed-in user through `ingest_capture`, and RLS
 * decides what happens to it. A page could declare `module: "billing"` while
 * showing something else; the consequence is a mislabelled capture of that same
 * page, which is a triage annoyance and not a security event. Worth stating,
 * because a field named `record_id` invites being read as a fact.
 *
 * ── nulls are removed, never emitted as "null" ─────────────────────────────
 * `JSON.stringify({a: undefined})` drops the key, but `{a: null}` keeps it, and
 * a consumer reading `record_id: "null"` would sort captures under a record with
 * that id forever. Only present, non-empty strings travel.
 */

/** Public page declaration consumed through `@solum/capture-client`. */
export interface DeclaredContext {
  app?: string
  version?: string
  module?: string
  entity?: string
  record_id?: string
  product?: string
  route?: string
}

const META = 'solum-context'

/**
 * Write (or clear) the declared context.
 *
 * Idempotent and cheap: it reuses the one tag rather than adding another, so a
 * router that fires twice for one navigation cannot leave two behind — and two
 * would mean the extension reads whichever the DOM returns first, which is not a
 * decision anybody made.
 */
export function declareContext(ctx: DeclaredContext | null): void {
  if (typeof document === 'undefined') return   // SSR / tests without a DOM

  const clean: Record<string, string> = {}
  for (const [k, v] of Object.entries(ctx ?? {})) {
    if (typeof v === 'string' && v.trim() !== '') clean[k] = v.trim()
  }

  let tag = document.querySelector<HTMLMetaElement>(`meta[name="${META}"]`)

  // Nothing to say ⇒ REMOVE the tag rather than leave `{}` behind. An empty
  // declaration and a stale one are indistinguishable to the reader, and the
  // stale one is what a route change leaves if this only ever wrote.
  if (Object.keys(clean).length === 0) {
    tag?.remove()
    return
  }

  if (!tag) {
    tag = document.createElement('meta')
    tag.name = META
    document.head.appendChild(tag)
  }
  tag.content = JSON.stringify(clean)
}
